// NoteViewV2 — entry point for all notes served at /patient/:patientId/note/:noteId/v2.
//
// "V2" refers to the route suffix, not to note.version. Notes here carry an explicit
// note_type field and/or a version number (1–7); NoteContentRouter dispatches
// to the appropriate renderer based on those fields:
//
//   note_type "Care Management"         → CareManagementForm
//   note_type "Advanced Care Activity"  → AdvancedCareActivityView
//   version 2                           → GuidedSessionView  (BHS / Prescriber guided session)
//   version 3                           → CheckInView  (BHS / Prescriber)
//   version 4                           → CareCoordinationView
//   version 5, type ≠ "Discharge"       → CareCommunicationView  (Care Communication / Coordination)
//   version 6                           → CaseManagementView
//   version 1 / fallback                → SessionNoteView  (earliest versioned BHS / Prescriber)
//
// Pre-note-type era notes (no note_type, no version) are handled by NoteDetailPage at the
// unversioned route instead.
import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { AddendumList } from "@/features/addendum/AddendumList";
import { BackToPatientProfile } from "@/components/Common/BackToPatientProfile";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { usePatientDetails } from "@/features/patient/api/queries/usePatientDetails";
import type { UUID } from "@/core/types";

import { BaseNoteLayout } from "./components/BaseNoteLayout";
import { CosignatureReviewSection } from "./components/(cosignature)/CosignatureReviewSection";
import { DeleteNote } from "./components/DeleteNote";
import { MemberHeader } from "./components/MemberHeader";
import { NoteActionsBar } from "./components/NoteActionsBar";
import { useDeleteNote } from "./api/mutations/useDeleteNote";
import { useNoteDetails } from "./api/queries/useNoteDetails";

export function NoteViewV2() {
  const { noteId, patientId } = useParams<{ noteId: UUID; patientId: UUID }>();
  const profile = useSelector(selectProfile);
  const history = useHistory();

  const { data: note, refetch: refetchNote } = useNoteDetails({ noteId, patientId });
  const { data: patient } = usePatientDetails({ patientId });

  const [showDeleteNotePopUp, setShowDeleteNotePopUp] = useState(false);

  const [draftNoteId, setDraftNoteId] = useState<UUID | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const { mutate: deleteNote } = useDeleteNote({
    onSuccess: () => {
      setShowDeleteNotePopUp(false);
      goBack();
    },
  });

  const isNoteEditable = () => {
    if (!note) return false;

    return (
      (note.note_state === "draft" || note.note_state === "rejected") &&
      profile?.username === note.created_by
    );
  };

  const goBack = () => {
    history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
  };

  const isEditable = isNoteEditable();

  return (
    <>
      <BackToPatientProfile patientId={patientId} />

      {showDeleteNotePopUp && (
        <DeleteNote
          clickHandler={() =>
            draftNoteId && deleteNote({ noteId: draftNoteId, patientId: patientId as UUID })
          }
          close={() => setShowDeleteNotePopUp(false)}
        />
      )}

      {note && (
        <NoteActionsBar
          isEditable={isEditable}
          isEditing={isEditing}
          note={note}
          patientId={patientId}
          setDraftNoteId={setDraftNoteId}
          setIsEditing={setIsEditing}
          setShowDeleteNotePopUp={setShowDeleteNotePopUp}
        />
      )}

      <MemberHeader patientId={patientId} />

      <BaseNoteLayout
        isEditing={isEditing}
        note={note}
        patientName={patient ? `${patient.first_name} ${patient.last_name}` : undefined}
        refetchNote={refetchNote}
        setIsEditing={setIsEditing}
      />

      {note && note.patient_id && <AddendumList note={note} />}
      {note && note.patient_id && <CosignatureReviewSection note={note} />}
    </>
  );
}
